import { Grid, Paper } from "@mui/material";
import { RowClassParams, RowStyle, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useRef, useState } from "react";
import { CommentModal } from "@/controls/ag-grid/components/CommentModal";
import { ProcessByShift } from "@/pages/meltshop/models/ProcessByShift";
import { useEAFColumns } from "../hooks/useEAFColumns";
import { useLMFColumns } from "../hooks/useLMFColumns";
import { useCasterColumns } from "../hooks/useCasterColumns";
import { ASSET_IDS } from "@/utils/constants";
import { useResize } from "@/utils/useResize";
import { getResponsiveTableStyles } from "@/utils/responsive-utils";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { upsertProcessData } from "../repositories/ProductionReportRepository";

type Props = {
  loadingGrid: boolean;
  rows: ProcessByShift[];
  onGridReady?: (params: GridReadyEvent) => void;
  refreshData: () => Promise<void>;
  assetId: number;
};

export const ProductionReportTable = ({
  loadingGrid,
  rows,
  onGridReady,
  refreshData,
  assetId,
}: Props) => {
  const gridRef = useRef<AgGridReact>(null);
  const { width, height } = useResize();
  const tableStyles = getResponsiveTableStyles(width, height);

  // Comment modal state
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<ProcessByShift | null>(
    null,
  );
  const [modalPosition, setModalPosition] = useState<
    { top: number; left: number } | undefined
  >();

  // Wrapper function to adapt upsertProcessData signature for CommentModal
  const handleUpsertProcessData = (
    processId: number,
    field: string,
    value: unknown,
    ...args: unknown[]
  ) => {
    const asset = args[0] as number;
    return upsertProcessData(processId, field, value, asset);
  };

  const columnProps = {
    showCommentModal,
    setShowCommentModal: (
      data: ProcessByShift,
      position: { top: number; left: number },
    ) => {
      setSelectedProcess(data);
      setModalPosition(position);
      setShowCommentModal(true);
    },
  };

  const { columns: eafColumns } = useEAFColumns(columnProps);
  const { columns: lmfColumns } = useLMFColumns(columnProps);
  const { columns: casterColumns } = useCasterColumns(columnProps);

  const getColumns = () => {
    switch (assetId) {
      case ASSET_IDS.EAF:
        return eafColumns;
      case ASSET_IDS.LMF:
        return lmfColumns;
      case ASSET_IDS.CASTER:
        return casterColumns;
      default:
        return eafColumns;
    }
  };

  const columns = getColumns();

  const getRowStyle = (
    params: RowClassParams<ProcessByShift>,
  ): RowStyle | undefined => {
    if (!params.data?.IsActive) {
      return { background: "#FFEBEE", color: "#666" };
    }
    return undefined;
  };

  return (
    <>
      <Grid container>
        <Grid
          item
          component={Paper}
          elevation={3}
          md={12}
          xs={12}
          style={tableStyles}
        >
          <AgGridReact
            ref={gridRef}
            rowData={rows}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            getRowStyle={getRowStyle}
            loading={loadingGrid}
            onGridReady={onGridReady}
            {...defaultGridOptions}
          />
        </Grid>
      </Grid>

      <CommentModal
        show={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        refreshData={refreshData}
        position={modalPosition}
        selectedElement={
          selectedProcess
            ? { ...selectedProcess, ElementId: selectedProcess.ProcessId }
            : null
        }
        setSelectedElement={setSelectedProcess}
        upsertFunction={handleUpsertProcessData}
        additionalArgs={[assetId]}
      />
    </>
  );
};
